import React, { Component } from 'react';
import { Row, Col, FormGroup, Label } from 'reactstrap';
import { Spinner } from 'selfly-ui-react';
import ConfirmPopover from './confirmPopover';

const DEFAULT_VALUES = {
    setTargetTemperature: -18,
    setMinTargetTemperature: -25,
    setMaxTargetTemperature: -10,
    setDefrostEndTemperature: 5,
    setDefrostInterval: 8,
    setDefrostMaxLength: 30,
    setDefrostStartDelay: 10,
};

class RemoteControllerSettings extends Component {
    constructor(props) {
        super(props);
        const rc = props.remoteController;
        this.state = {
            setTargetTemperature: rc?.targetTemperature ?? DEFAULT_VALUES.setTargetTemperature,
            setMinTargetTemperature: rc?.minTargetTemperature ?? DEFAULT_VALUES.setMinTargetTemperature,
            setMaxTargetTemperature: rc?.maxTargetTemperature ?? DEFAULT_VALUES.setMaxTargetTemperature,
            setDefrostEndTemperature: rc?.defrostEndTemperature ?? DEFAULT_VALUES.setDefrostEndTemperature,
            setDefrostInterval: rc?.defrostInterval != null ? rc.defrostInterval : DEFAULT_VALUES.setDefrostInterval,
            setDefrostMaxLength: rc?.defrostMaxLen ?? DEFAULT_VALUES.setDefrostMaxLength,
            setDefrostStartDelay: rc?.defrostStartDelay ?? DEFAULT_VALUES.setDefrostStartDelay,
        };
    }

    handleChange = (field, value) => {
        this.setState({ [field]: value === '' ? '' : Number(value), validationError: null });
    };

    getFieldRange = (field) => {
        const { setTargetTemperature, setMinTargetTemperature, setMaxTargetTemperature } = this.state;
        switch (field) {
            case 'setTargetTemperature':
                return {
                    min: setMinTargetTemperature !== '' ? Number(setMinTargetTemperature) : -55,
                    max: setMaxTargetTemperature !== '' ? Number(setMaxTargetTemperature) : 150,
                };
            case 'setMinTargetTemperature':
                return { min: -55, max: setTargetTemperature !== '' ? Number(setTargetTemperature) : 150 };
            case 'setMaxTargetTemperature':
                return { min: setTargetTemperature !== '' ? Number(setTargetTemperature) : -55, max: 150 };
            case 'setDefrostEndTemperature':
                return { min: -55, max: 50 };
            case 'setDefrostInterval':
            case 'setDefrostMaxLength':
            case 'setDefrostStartDelay':
                return { min: 0, max: 255 };
            default:
                return {};
        }
    };

    validate = () => {
        const { isRoot } = this.props;
        const fields = isRoot
            ? Object.keys(this.state).filter(k => k !== 'validationError')
            : ['setTargetTemperature'];

        for (const field of fields) {
            const value = this.state[field];
            if (value === '' || value === null) continue;
            const { min, max } = this.getFieldRange(field);
            if (min != null && Number(value) < min) {
                return `${field}: value ${value} is below minimum ${min}`;
            }
            if (max != null && Number(value) > max) {
                return `${field}: value ${value} exceeds maximum ${max}`;
            }
        }
        return null;
    };

    handleSubmit = () => {
        const error = this.validate();
        if (error) {
            this.setState({ validationError: error });
            return;
        }

        const { isRoot } = this.props;
        const params = {};

        const roundValue = (v) => {
            const num = Number(v);
            return Number.isInteger(num) ? num : parseFloat(num.toFixed(1));
        };

        if (isRoot) {
            Object.keys(this.state).forEach(key => {
                if (key === 'validationError') return;
                if (this.state[key] !== '' && this.state[key] !== null) {
                    params[key] = roundValue(this.state[key]);
                }
            });
        } else {
            if (this.state.setTargetTemperature !== '' && this.state.setTargetTemperature !== null) {
                params.setTargetTemperature = roundValue(this.state.setTargetTemperature);
            }
        }

        this.props.onSubmit(this.props.deviceCode, params);
    };

    renderValue = (value, unit) => {
        if (value == null) return '-';
        const display = typeof value === 'number' && !Number.isInteger(value)
            ? parseFloat(value.toFixed(1))
            : value;
        return unit ? `${display} ${unit}` : display;
    };

    render() {
        const { t, inProgress, isRoot, remoteController: rc } = this.props;

        return (
            <div className="">
                <div className="alert alert-warning mb-3 mt-0 py-2">
                    {t('coolingControlWarning')}
                </div>
                {isRoot && (
                    <React.Fragment>
                        <h5>{t('currentRemoteControllerValues')}</h5>
                        {rc ? (
                            <div className="mb-4">
                                <Row>
                                    <Col md="4">
                                        <div className="mb-2">
                                            <small className="text-muted">{t('temperature')}</small>
                                            <div><strong>{this.renderValue(rc.temperature, '°C')}</strong></div>
                                        </div>
                                    </Col>
                                    <Col md="4">
                                        <div className="mb-2">
                                            <small className="text-muted">{t('intakeTemperature')}</small>
                                            <div><strong>{this.renderValue(rc.intakeTemperature, '°C')}</strong></div>
                                        </div>
                                    </Col>
                                    <Col md="4">
                                        <div className="mb-2">
                                            <small className="text-muted">{t('exhaustTemperature')}</small>
                                            <div><strong>{this.renderValue(rc.exhaustTemperature, '°C')}</strong></div>
                                        </div>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md="4">
                                        <div className="mb-2">
                                            <small className="text-muted">{t('thermostatTemperature')}</small>
                                            <div><strong>{this.renderValue(rc.thermostatTemperature, '°C')}</strong></div>
                                        </div>
                                    </Col>
                                    <Col md="4">
                                        <div className="mb-2">
                                            <small className="text-muted">{t('evaporatorTemperature')}</small>
                                            <div><strong>{this.renderValue(rc.evaporatorTemperature, '°C')}</strong></div>
                                        </div>
                                    </Col>
                                    <Col md="4">
                                        <div className="mb-2">
                                            <small className="text-muted">{t('defrostDrainTime')}</small>
                                            <div><strong>{this.renderValue(rc.defrostDrainTime)}</strong></div>
                                        </div>
                                    </Col>
                                </Row>
                            </div>
                        ) : (
                            <p className="text-muted mb-4">{t('noRemoteControllerData')}</p>
                        )}
                        <hr />
                    </React.Fragment>
                )}

                <h5>{t('targetTemperature')}</h5>
                <Row>
                    <Col md="6">
                        <FormGroup>
                            <Label>{t('targetTemperature')} (°C) {rc && <small className="text-muted">({t('currentValue')}: <span style={{ color: '#28a745' }}>{this.renderValue(rc.targetTemperature, '°C')}</span>)</small>}</Label>
                            <input
                                type="number"
                                className="form-control"
                                min={this.getFieldRange('setTargetTemperature').min}
                                max={this.getFieldRange('setTargetTemperature').max}
                                value={this.state.setTargetTemperature}
                                onChange={e => this.handleChange('setTargetTemperature', e.target.value)}
                            />
                        </FormGroup>
                    </Col>
                    {isRoot && (
                        <Col md="6">
                            <FormGroup>
                                <Label>{t('defrostEndTemperature')} (°C) {rc && <small className="text-muted">({t('currentValue')}: <span style={{ color: '#28a745' }}>{this.renderValue(rc.defrostEndTemperature, '°C')}</span>)</small>}</Label>
                                <input
                                    type="number"
                                    className="form-control"
                                    min={-55}
                                    max={50}
                                    value={this.state.setDefrostEndTemperature}
                                    onChange={e => this.handleChange('setDefrostEndTemperature', e.target.value)}
                                />
                            </FormGroup>
                        </Col>
                    )}
                </Row>
                {isRoot && (
                    <Row>
                        <Col md="6">
                            <FormGroup>
                                <Label>{t('minTargetTemperature')} (°C) {rc && <small className="text-muted">({t('currentValue')}: <span style={{ color: '#28a745' }}>{this.renderValue(rc.minTargetTemperature, '°C')}</span>)</small>}</Label>
                                <input
                                    type="number"
                                    className="form-control"
                                    min={-55}
                                    max={this.getFieldRange('setMinTargetTemperature').max}
                                    value={this.state.setMinTargetTemperature}
                                    onChange={e => this.handleChange('setMinTargetTemperature', e.target.value)}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="6">
                            <FormGroup>
                                <Label>{t('maxTargetTemperature')} (°C) {rc && <small className="text-muted">({t('currentValue')}: <span style={{ color: '#28a745' }}>{this.renderValue(rc.maxTargetTemperature, '°C')}</span>)</small>}</Label>
                                <input
                                    type="number"
                                    className="form-control"
                                    min={this.getFieldRange('setMaxTargetTemperature').min}
                                    max={150}
                                    value={this.state.setMaxTargetTemperature}
                                    onChange={e => this.handleChange('setMaxTargetTemperature', e.target.value)}
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                )}

                {isRoot && (
                    <React.Fragment>
                        <h5 className="mt-3">{t('defrostSettings')}</h5>
                        <Row>
                            <Col md="4">
                                <FormGroup>
                                    <Label>{t('defrostInterval')} {rc && <small className="text-muted">({t('currentValue')}: <span style={{ color: '#28a745' }}>{this.renderValue(rc.defrostInterval)}</span>)</small>}</Label>
                                    <input
                                        type="number"
                                        min={0}
                                        max={255}
                                        className="form-control"
                                        value={this.state.setDefrostInterval}
                                        onChange={e => this.handleChange('setDefrostInterval', e.target.value)}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md="4">
                                <FormGroup>
                                    <Label>{t('defrostMaxLength')} {rc && <small className="text-muted">({t('currentValue')}: <span style={{ color: '#28a745' }}>{this.renderValue(rc.defrostMaxLen)}</span>)</small>}</Label>
                                    <input
                                        type="number"
                                        min={0}
                                        max={255}
                                        className="form-control"
                                        value={this.state.setDefrostMaxLength}
                                        onChange={e => this.handleChange('setDefrostMaxLength', e.target.value)}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md="4">
                                <FormGroup>
                                    <Label>{t('defrostStartDelay')} {rc && <small className="text-muted">({t('currentValue')}: <span style={{ color: '#28a745' }}>{this.renderValue(rc.defrostStartDelay)}</span>)</small>}</Label>
                                    <input
                                        type="number"
                                        min={0}
                                        max={255}
                                        className="form-control"
                                        value={this.state.setDefrostStartDelay}
                                        onChange={e => this.handleChange('setDefrostStartDelay', e.target.value)}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                    </React.Fragment>
                )}

                {this.state.validationError && (
                    <div className="text-danger mb-2">{this.state.validationError}</div>
                )}

                <div className="mt-3">
                    <ConfirmPopover
                        icon="send"
                        disabled={inProgress}
                        title={t('sendRemoteControllerSettings')}
                        confirm={t('remoteControllerConfirm')}
                        action={() => this.handleSubmit()}
                        style={{ background: '#007bff' }}
                    />
                    {isRoot && (
                        <ConfirmPopover
                            icon="refresh"
                            disabled={inProgress}
                            title={t('rebootRemoteController')}
                            confirm={t('rebootRemoteControllerConfirm')}
                            action={() => this.props.onReboot(this.props.deviceCode)}
                            style={{ background: '#67B419' }}
                        />
                    )}
                    {inProgress && <Spinner className="ml-2" />}
                </div>
            </div>
        );
    }
}

export default RemoteControllerSettings;
